/**
 * OHD employer dashboard chart colors.
 *
 * Hex literals are kept here instead of being inlined across components
 * so future theming work has a single place to change. These are not
 * tenant-themable (OHD palette is fixed across deployments) — promoting
 * to CSS variables is unnecessary today.
 *
 * `colorFor` deliberately matches by recommendation name first so the
 * cardiometabolic / cardiac / diabetic / GLP categories stay stable
 * across tier changes, and falls back to `TIER_COLOR` when no name
 * override matches.
 */
export const TIER_COLOR: Record<string, string> = {
    critical: '#e8919e',
    high: '#e8a84e',
    moderate: '#9589c4',
    low: '#83d7e1',
};

const NAME_OVERRIDES: Array<{match: string; color: string}> = [
    {match: 'cardiometabolic', color: '#e8919e'},
    {match: 'cardiac', color: '#e8919e'},
    {match: 'diabetic', color: '#9589c4'},
    {match: 'glp', color: '#e8a84e'},
];

const DEFAULT_PROGRAM_COLOR = '#9589c4';

export function colorForProgram(name: string, tier: string | null): string {
    const lower = name.toLowerCase();
    for (const override of NAME_OVERRIDES) {
        if (lower.includes(override.match)) return override.color;
    }
    return tier ? TIER_COLOR[tier] ?? DEFAULT_PROGRAM_COLOR : DEFAULT_PROGRAM_COLOR;
}

/** Accent color used for the dashboard's underline accent (low tier). */
export const ACCENT_LAB_TREND = '#83d7e1';

/**
 * Risk-tile colors used on RiskAssessmentCard. One per risk category;
 * order matches OverallRiskIcon / CardiacRiskIcon / DiabeticRiskIcon /
 * ComorbidityRiskIcon.
 */
export const RISK_TILE_COLORS = {
    overall: '#fbc472',
    cardiac: '#ef839d',
    diabetic: '#c7b8df',
    comorbidity: '#7ab8dd',
} as const;

/** Donut chart track color used across OHD risk + program donuts. */
export const DONUT_TRACK = '#dddcd6';

/**
 * Screening-modality palette. Keys match OhdResultSetCounts fields.
 */
export const SCREENING_MODALITY_COLORS = {
    lab: '#8bded6',
    physician: '#2cb5a0',
    home_test_kit: '#00b7a3',
    onsite: '#009e8c',
} as const;

/** Dark navy used for OHD risk icon strokes + menu chrome on the OHD pages. */
export const OHD_ICON_STROKE = '#1a3a4a';
